import type { Delta, Op } from 'quill/core';
import React, { useState } from 'react';
import EmojiAutocompleter from './controls/EmojiAutocompleter';
import MentionPicker from './controls/MentionPicker';
import SlashCommandMenu from './controls/SlashCommandMenu';
import EditorToolbar from './controls/toolbar/EditorToolbar';
import QuillComponent from './QuillComponent';
import type QuillComponentProps from './QuillComponentProps';
import QuillContext from './QuillContext';
import type QuillEditor from './QuillEditor';
import * as styles from './RichTextDescriptionEditor.module.scss';
import HoverContentPreview from '../post/HoverContentPreview';
import addTestId from '../utils/addTestId';

interface RichTextDescriptionEditorProps {
  autoFocus?: boolean;
  noTitleLine?: boolean;
  titlePlaceholder?: string;
  contentPlaceholder?: string;
  namespace: QuillComponentProps['namespace'];
  defaultValue: Delta | Op[];
  readOnly: boolean;
  onMount?: (quillEditor: QuillEditor) => void;
  onTextChange?: (op: Op[]) => void;
  testId?: string;
}

const RichTextDescriptionEditor = ({
  autoFocus,
  noTitleLine = false,
  titlePlaceholder,
  contentPlaceholder,
  namespace,
  defaultValue,
  readOnly,
  onMount,
  onTextChange,
  testId,
}: RichTextDescriptionEditorProps) => {
  const [quill, setQuill] = useState<QuillEditor | null>(null);

  return (
    <div className={styles.container} {...addTestId(testId)}>
      <QuillContext>
        {quill && !readOnly && (
          <>
            <EditorToolbar readOnly={readOnly} />
            <MentionPicker quill={quill} readOnly={readOnly} />
            <EmojiAutocompleter quill={quill} readOnly={readOnly} />
          </>
        )}
        <HoverContentPreview>
          <QuillComponent
            autoFocus={autoFocus}
            noTitleLine={noTitleLine}
            titlePlaceholder={titlePlaceholder}
            contentPlaceholder={contentPlaceholder}
            namespace={namespace}
            moduleConfig={{
              prefixListener: readOnly ? null : true,
            }}
            rootClassName={styles.editorRoot}
            containerClassName={styles.editorContainer}
            initialContent={defaultValue}
            readOnly={readOnly}
            onTextChange={() => {
              if (quill && onTextChange) {
                const actualContent = quill.getContents().ops;
                onTextChange(actualContent);
              }
            }}
            onMount={(mounted) => {
              setQuill(mounted);
              if (onMount) onMount(mounted);
            }}
          />
        </HoverContentPreview>
        {quill && !readOnly && (
          <SlashCommandMenu quill={quill} readOnly={readOnly} />
        )}
      </QuillContext>
    </div>
  );
};

export default RichTextDescriptionEditor;
